import { RenovationExecutor } from 'shared/constants/space-renovation-requests';
import formatDate from 'shared/utils/formatDate';

import type {
  CreateFormOptions,
  RenovationTypeCreateModel,
} from 'pages/repair/types';
import type {
  FormRenovationType,
  RenovationTypeOption,
} from './SpaceRenovationRequestCreateForm.type';

import {
  CODES_LEVEL,
  RenovationTypesCodesLabels,
  RenovationTypeCode,
} from './constants';

export function prepareRenovationTypes(
  baseRenovationTypes: FormRenovationType[]
) {
  let payload: RenovationTypeCreateModel[] = [];

  for (let baseRenovationType of baseRenovationTypes) {
    if (!baseRenovationType.executorType || !baseRenovationType.renovationType)
      continue;

    let requiredFields: Pick<
      RenovationTypeCreateModel,
      'Description' | 'ExecutorType' | 'Type'
    > = {
      Description: baseRenovationType.description,
      ExecutorType: baseRenovationType.executorType,
      Type: baseRenovationType.renovationType.value,
    };

    if (baseRenovationType.executorType === RenovationExecutor.Organization) {
      payload.push({
        ...requiredFields,
        BusinessId: baseRenovationType.organizationBusinessId,
        Email: baseRenovationType.organizationEmail,
        MobilePhone: baseRenovationType.organizationPhone,
        OrganizationName: baseRenovationType.organizationName,
      });

      continue;
    }

    if (baseRenovationType.executorType === RenovationExecutor.ForeignCompany) {
      payload.push({
        ...requiredFields,
        ForeignRegistrationNumber: baseRenovationType.foreignRegistrationNumber,
        Email: baseRenovationType.organizationEmail,
        MobilePhone: baseRenovationType.organizationPhone,
        OrganizationName: baseRenovationType.organizationName,
      });

      continue;
    }

    if (baseRenovationType.executorType === RenovationExecutor.Person) {
      payload.push({
        ...requiredFields,
        Birthday: baseRenovationType.personBirthday
          ? formatDate(baseRenovationType.personBirthday, {
              patternName: 'server',
            })
          : undefined,
        Email: baseRenovationType.personEmail,
        FirstName: baseRenovationType.personFirstName,
        LastName: baseRenovationType.personLastName,
        MobilePhone: baseRenovationType.personPhone,
      });

      continue;
    }

    if (baseRenovationType.executorType === RenovationExecutor.PortalUser) {
      payload.push({
        ...requiredFields,
        Birthday: baseRenovationType.portalUserBirthday
          ? formatDate(baseRenovationType.portalUserBirthday, {
              patternName: 'server',
            })
          : undefined,
        Email: baseRenovationType.portalUserEmail,
        FirstName: baseRenovationType.portalUserFirstName,
        LastName: baseRenovationType.portalUserLastName,
        MobilePhone: baseRenovationType.portalUserPhone,
      });

      continue;
    }

    payload.push({ ...requiredFields });
  }

  return payload;
}

export function createBaseRenovationType() {
  const baseRenovationType: FormRenovationType = {
    description: '',
    executorType: null,
    renovationType: null,
  };

  return baseRenovationType;
}

export function getRenovationTypeOrder(renovationTypeCode: RenovationTypeCode) {
  switch (renovationTypeCode) {
    case RenovationTypeCode.AirHeatPumpInstallation:
      return 1;
    case RenovationTypeCode.ElectricAndHybridCarCharging:
      return 2;
    default:
      return 3;
  }
}

function sortRenovationTypeOption(
  renovationTypeOptions: RenovationTypeOption[]
) {
  return [...renovationTypeOptions].sort((a, b) => {
    const aOrder = getRenovationTypeOrder(a.value);
    const bOrder = getRenovationTypeOrder(b.value);

    return aOrder - bOrder;
  });
}

export function getRenovationTypeOptions(
  renovationTypesCodes: CreateFormOptions['RenovationTypesCodes']
) {
  if (!renovationTypesCodes) return [];

  const low: RenovationTypeOption[] = [];
  const normal: RenovationTypeOption[] = [];
  const high: RenovationTypeOption[] = [];
  const other: RenovationTypeOption[] = [];

  for (let { Label, Value } of renovationTypesCodes) {
    if (CODES_LEVEL.low.includes(Value)) {
      low.push({
        label: Label,
        parent: RenovationTypesCodesLabels.Low,
        value: Value,
      });
    }

    if (CODES_LEVEL.normal.includes(Value)) {
      normal.push({
        label: Label,
        parent: RenovationTypesCodesLabels.Normal,
        value: Value,
      });
    }

    if (CODES_LEVEL.high.includes(Value)) {
      high.push({
        label: Label,
        parent: RenovationTypesCodesLabels.High,
        value: Value,
      });
    }

    if (CODES_LEVEL.other.includes(Value)) {
      other.push({
        label: Label,
        parent: RenovationTypesCodesLabels.Other,
        value: Value,
      });
    }
  }

  return [...sortRenovationTypeOption(high), ...normal, ...low, ...other];
}
